import React, { useState } from 'react';
import { Alert, Box, Button, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SquareEnvironment, SquareService } from '../../../api';
import { OrderPageButton } from '../../themedComponents/OrderPageButton';
import { OrderPageTypography } from '../../themedComponents/OrderPageTypography';
import { formatUsd, SquarePaymentFormData } from './squarePaymentForm';
import {
  ALREADY_PAID_PROVIDER_ERROR_ID,
  CHARGED_NOT_BOOKED_PROVIDER_ERROR_ID,
  CHECKOUT_EXPIRED_PROVIDER_ERROR_ID,
  DOUBLE_CHARGE_PROVIDER_ERROR_ID,
  providerErrorId,
  SQUARE_ERROR_TEXT_KEYS,
  squareProviderErrorTextKey,
  SquareIntent,
  tokenizeSquareCard,
  UNKNOWN_OUTCOME_PROVIDER_ERROR_ID,
  useSquareSdk,
} from './useSquareSdk';

const BAD_GATEWAY_STATUS = 502;

export enum SquareFrameVariant {
  WIDGET = 'widget',
  PORTAL = 'portal',
}

type SquarePaymentFrameProps = {
  form: SquarePaymentFormData;
  language: string;
  variant: SquareFrameVariant;
  onPaid: (redirectUrl: string) => void;
  onFormExpired: () => void;
  businessName?: string;
  payLabelKey?: string;
  nextChargeDate?: string;
};

export const SquarePaymentFrame = ({
  form,
  language,
  variant,
  onPaid,
  onFormExpired,
  businessName,
  payLabelKey = 'order_page.square.pay',
  nextChargeDate,
}: SquarePaymentFrameProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isWidget = variant === SquareFrameVariant.WIDGET;
  const [errorTextKey, setErrorTextKey] = useState<string | null>(null);
  const [isPaying, setIsPaying] = useState(false);
  const [isPaymentUnresolved, setIsPaymentUnresolved] = useState(false);

  const widgetPalette = isWidget ? theme.customTheme.palette : undefined;
  const { cardContainerRef, card, hasLoadFailed } = useSquareSdk({
    environment: form.environment,
    applicationId: form.application_id,
    locationId: form.location_id,
    cardStyle: widgetPalette && {
      '.input-container': { borderColor: widgetPalette.border, borderRadius: '8px' },
      input: { color: widgetPalette.text.primary, backgroundColor: widgetPalette.background },
      'input::placeholder': { color: widgetPalette.text.secondary },
    },
  });

  const totalAmount = form.amount + form.tip_amount + (form.booking_fee_amount ?? 0);
  const payText = isPaying ? t('order_page.square.verifying') : t(payLabelKey, { amount: formatUsd(totalAmount) });

  const handlePay = async () => {
    if (!card) {
      return;
    }
    setIsPaying(true);
    setErrorTextKey(null);
    const tokenized = await tokenizeSquareCard(card, {
      amount: totalAmount.toFixed(2),
      currencyCode: form.currency,
      intent: form.save_card ? SquareIntent.CHARGE_AND_STORE : SquareIntent.CHARGE,
      billingContact: {
        givenName: form.customer_given_name,
        familyName: form.customer_family_name,
        email: form.customer_email,
      },
      customerInitiated: true,
      sellerKeyedIn: false,
    });
    if ('errorTextKey' in tokenized) {
      setErrorTextKey(tokenized.errorTextKey);
      setIsPaying(false);
      return;
    }
    try {
      const { redirect_url: redirectUrl } = await SquareService.squarePay({
        checkout_token: form.checkout_token,
        source_id: tokenized.token,
        idempotency_key: crypto.randomUUID().slice(0, 36),
        language,
      });
      onPaid(redirectUrl);
    } catch (e: any) {
      console.error(e);
      const errorId = providerErrorId(e);
      if (errorId === CHECKOUT_EXPIRED_PROVIDER_ERROR_ID) {
        onFormExpired();
      } else {
        const isOutcomeUnknown = errorId === UNKNOWN_OUTCOME_PROVIDER_ERROR_ID || e?.status === BAD_GATEWAY_STATUS;
        setErrorTextKey(isOutcomeUnknown ? SQUARE_ERROR_TEXT_KEYS.unknownOutcome : squareProviderErrorTextKey(e));
        setIsPaymentUnresolved(
          isOutcomeUnknown ||
            errorId === CHARGED_NOT_BOOKED_PROVIDER_ERROR_ID ||
            errorId === ALREADY_PAID_PROVIDER_ERROR_ID ||
            errorId === DOUBLE_CHARGE_PROVIDER_ERROR_ID,
        );
      }
      setIsPaying(false);
    }
  };

  const isPayDisabled = isPaying || !card || isPaymentUnresolved;
  const shownErrorTextKey = hasLoadFailed ? SQUARE_ERROR_TEXT_KEYS.unavailable : errorTextKey;
  const savedCardNote =
    form.save_card && businessName ? t('order_page.square.savedCardNote', { business: businessName }) : null;
  const securedCaption = [
    nextChargeDate && t('manage_passes.buy.nextCharge', { date: nextChargeDate }),
    t('order_page.square.secured'),
  ]
    .filter(Boolean)
    .join(' ');

  const renderText = (text: string, textVariant: 'sandbox' | 'hint' | 'caption') => {
    if (isWidget) {
      return textVariant === 'sandbox' ? (
        <OrderPageTypography color="error">{text}</OrderPageTypography>
      ) : (
        <OrderPageTypography
          style={{
            color: theme.customTheme.palette.text.secondary,
            ...(textVariant === 'caption' ? { textAlign: 'center', fontSize: 12 } : {}),
          }}>
          {text}
        </OrderPageTypography>
      );
    }
    return (
      <Typography
        variant={textVariant === 'caption' ? 'caption' : 'body2'}
        color={textVariant === 'sandbox' ? 'error' : 'text.secondary'}
        textAlign={textVariant === 'caption' ? 'center' : undefined}
        fontWeight={textVariant === 'sandbox' ? 700 : undefined}>
        {text}
      </Typography>
    );
  };

  const payButtonSx = isSmallScreen ? { width: '100%' } : { minWidth: '220px' };

  return (
    <Grid container flexDirection="column" gap={2}>
      {form.environment === SquareEnvironment.SANDBOX && (
        <Grid item container flexDirection="column" gap={0.5}>
          {renderText('SANDBOX', 'sandbox')}
          {renderText(t('order_page.square.sandboxHint'), 'hint')}
        </Grid>
      )}
      {shownErrorTextKey && (
        <Alert severity="error" role="alert">
          {t(shownErrorTextKey)}
        </Alert>
      )}
      <Box ref={cardContainerRef} sx={{ minHeight: '96px' }} />
      {savedCardNote && renderText(savedCardNote, 'hint')}
      <Grid item container justifyContent={isSmallScreen ? 'stretch' : 'flex-end'}>
        {isWidget ? (
          <OrderPageButton
            variant="contained"
            $disabled={isPayDisabled}
            disabled={isPayDisabled}
            sx={payButtonSx}
            onClick={() => {
              void handlePay();
            }}>
            <OrderPageTypography variant="h6" color="button.contained" bold>
              {payText}
            </OrderPageTypography>
          </OrderPageButton>
        ) : (
          <Button
            variant="contained"
            disabled={isPayDisabled}
            sx={payButtonSx}
            onClick={() => {
              void handlePay();
            }}>
            {payText}
          </Button>
        )}
      </Grid>
      {renderText(securedCaption, 'caption')}
    </Grid>
  );
};
